import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Container, Row, Col, Button } from "react-bootstrap";
import { toast } from "react-toastify";
import NavigationBar from "../components/NavigationBar";
import Footer from "../components/Footer";
import TrackerCard from "../components/TrackerCard";
import MonthCard from "../components/MonthCard";
import AddTrackerModal from "../components/AddTrackerModal";
import CheapestDaysModal from "../components/CheapestDaysModal";
import FlexibleSearchModal from "../components/FlexibleSearchModal";
import PriceChartModal from "../components/PriceChartModal";
import FlightSelectionModal from "../components/FlightSelectionModal";
import ConfirmDialog from "../components/ConfirmDialog";
import { useI18n } from "../i18n";
import { useNotifications } from "../contexts/NotificationContext";
import axios from "axios";

export interface Tracker {
  id: number;
  origin: string;
  origin_city?: string | null;
  origin_country?: string | null;
  destination: string;
  destination_city?: string | null;
  destination_country?: string | null;
  departure_date: string;
  return_date: string | null;
  original_departure_date?: string; // Track user's original selection
  original_return_date?: string | null; // Track user's original selection
  threshold_price: number | null;
  alert_sent: boolean;
  current_price: number | null;
  price_history: Array<{ timestamp: string; price: number }>;
  flight_details?: {
    type: "round_trip" | "one_way";
    outbound: {
      flight_number: string;
      price: number;
      original_price?: number;
      original_currency?: string;
      date: string;
      departure_time: string;
      arrival_time: string;
      duration: string;
      flight_key: string;
    };
    return: {
      flight_number: string;
      price: number;
      original_price?: number;
      original_currency?: string;
      date: string;
      departure_time: string;
      arrival_time: string;
      duration: string;
      flight_key: string;
    } | null;
    total_price: number;
    total_original_price?: number;
    currency: string;
    original_currency?: string;
  };
}

const Dashboard: React.FC = () => {
  const [trackers, setTrackers] = useState<Tracker[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddTracker, setShowAddTracker] = useState(false);
  const [showCheapestDays, setShowCheapestDays] = useState(false);
  const [showFlexibleSearch, setShowFlexibleSearch] = useState(false);
  const [showPriceChart, setShowPriceChart] = useState(false);
  const [showFlightSelection, setShowFlightSelection] = useState(false);
  const [selectedTracker, setSelectedTracker] = useState<Tracker | null>(null);
  const [editingFlightType, setEditingFlightType] = useState<
    "outbound" | "return"
  >("outbound");
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [trackerToDelete, setTrackerToDelete] = useState<number | null>(null);
  const [addingTracker, setAddingTracker] = useState(false);
  const [showConfirmDeleteMonth, setShowConfirmDeleteMonth] = useState(false);
  const [deleteMonthYear, setDeleteMonthYear] = useState<number | null>(null);
  const [deleteMonthMonth, setDeleteMonthMonth] = useState<number | null>(null);
  const [deleteMonthLabel, setDeleteMonthLabel] = useState<string>("");

  // i18n
  const { t, lang } = useI18n();
  const { notifications } = useNotifications();

  // Helper function to get month/year from date
  const getMonthYear = (dateString: string): string => {
    const date = new Date(dateString);
    const monthNames = lang === 'it' 
      ? ['Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno', 
         'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre']
      : ['January', 'February', 'March', 'April', 'May', 'June',
         'July', 'August', 'September', 'October', 'November', 'December'];
    return `${monthNames[date.getMonth()]} ${date.getFullYear()}`;
  };

  // Helper function to get sort key for month/year
  const getMonthYearSortKey = (dateString: string): string => {
    const date = new Date(dateString);
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
  };

  // Group trackers by month/year
  const groupedTrackers = useMemo(() => {
    const groups: { [key: string]: { sortKey: string; trackers: Tracker[] } } = {};
    
    trackers.forEach((tracker) => {
      const monthYear = getMonthYear(tracker.departure_date);
      const sortKey = getMonthYearSortKey(tracker.departure_date);
      
      if (!groups[monthYear]) {
        groups[monthYear] = { sortKey, trackers: [] };
      }
      groups[monthYear].trackers.push(tracker);
    });
    
    // Sort groups by date (earliest first) and include sortKey
    return Object.entries(groups)
      .sort(([, a], [, b]) => a.sortKey.localeCompare(b.sortKey))
      .map(([monthYear, data]) => ({
        monthYear,
        sortKey: data.sortKey,
        trackers: data.trackers,
      }));
  }, [trackers, lang]);

  // Get notification count for a month
  const getMonthNotificationCount = (monthTrackers: Tracker[]): number => {
    const trackerIds = monthTrackers.map(t => t.id);
    return notifications.filter(n => trackerIds.includes(n.tracker_id)).length;
  };

  const loadTrackers = useCallback(
    async (loadFlightDetails: boolean = true): Promise<Tracker[] | undefined> => {
      try {
        // Use query parameter for performance optimization
        const response = await axios.get("/api/trackers", {
          params: { load_flights: loadFlightDetails },
        });
        setTrackers(response.data);
        return response.data;
      } catch (error) {
        console.error("Error loading trackers:", error);
        toast.error(t("messages.loadingTrackersError"));
      } finally {
        setLoading(false);
      }
    },
    [t],
  );

  useEffect(() => {
    loadTrackers();
    const interval = setInterval(() => loadTrackers(), 300000); // Refresh every 5 minutes
    return () => clearInterval(interval);
  }, [loadTrackers]);

  

  // Listen for notification deletions to refresh trackers and remove alert indicators
  React.useEffect(() => {
    const handler = (e: Event) => {
      // reload trackers to pick up any alert_sent changes
      loadTrackers();
    };
    window.addEventListener("notificationDeleted", handler as EventListener);
    return () => {
      window.removeEventListener("notificationDeleted", handler as EventListener);
    };
  }, [loadTrackers]);

  

  const handleDeleteTracker = (id: number) => {
    setTrackerToDelete(id);
    setShowConfirmDelete(true);
  };

  const confirmDelete = async () => {
    if (trackerToDelete === null) return;

    try {
      await axios.delete(`/api/trackers/${trackerToDelete}`);
      toast.success(t("messages.trackerDeleted"));
      // Optimization: Don't load flight details after deletion (load_flights=false)
      await loadTrackers(false);
    } catch (error) {
      console.error("Error deleting tracker:", error);
      toast.error(t("messages.trackerDeleteError"));
    } finally {
      setShowConfirmDelete(false);
      setTrackerToDelete(null);
    }
  };

  const handleShowChart = async (tracker: Tracker) => {
    setSelectedTracker(tracker);
    setShowPriceChart(true);
    // Load fresh data in background without blocking modal opening
    loadTrackers(true).then((latest) => {
      const freshTracker = latest?.find((t) => t.id === tracker.id);
      if (freshTracker) {
        setSelectedTracker(freshTracker);
      }
    });
  };

  const handleEditFlight = (
    trackerId: number,
    flightType: "outbound" | "return",
  ) => {
    const tracker = trackers.find((t) => t.id === trackerId);
    if (tracker) {
      setSelectedTracker(tracker);
      setEditingFlightType(flightType);
      setShowFlightSelection(true);
    }
  };

  const handleFlightSelected = async () => {
    // Optimization: Only reload flight details for the specific tracker being edited
    await loadTrackers(true);
  };

  const handleTrackerAdded = (hasFlightData: boolean = false) => {
    setShowAddTracker(false);
    setAddingTracker(true);
    // If flight data already stored (from CheapestDaysModal), use loadTrackers(false) - fast, uses snapshot
    // If no flight data (from AddTrackerModal), use loadTrackers(true) - needs API call
    loadTrackers(!hasFlightData).finally(() => setAddingTracker(false));
  };

  const handleThresholdUpdated = async () => {
    // Use optimistic update - don't reload everything, just refresh price data
    await loadTrackers(false);
  };

  return (
    <>
      <NavigationBar />
      <Container>
        <Row className="mb-4">
          <Col>
            <h1>
              <i className="bi bi-speedometer2"></i> {t("nav.dashboard")}
            </h1>
            <p className="text-muted">{t("dashboard.monitoringSubtitle")}</p>
          </Col>
          <Col xs={12} md="auto" className="dashboard-header-buttons">
            <Button
              variant="primary"
              onClick={() => setShowAddTracker(true)}
              className="me-md-2 mb-2 mb-md-0"
            >
              <i className="bi bi-plus-circle"></i> {t("tracker.addTracker")}
            </Button>
            <Button
              variant="outline-primary"
              onClick={() => setShowCheapestDays(true)}
              className="me-md-2 mb-2 mb-md-0"
            >
              <i className="bi bi-calendar-check"></i> {t("tracker.findCheapestDays")}
            </Button>
            <Button
              variant="outline-secondary"
              onClick={() => setShowFlexibleSearch(true)}
            >
              <i className="bi bi-calendar-range"></i> {t("tracker.flexibleSearch")}
            </Button>
          </Col>
        </Row>

        <Row className="mb-4">
          <Col>
            <h3>{t("tracker.yourTrackers")}</h3>
                {loading ? (
              <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">{t("tracker.loading")}</span>
                </div>
              </div>
            ) : trackers.length === 0 ? (
              <div className="text-center py-5">
                <i
                  className="bi bi-inbox"
                  style={{ fontSize: "4rem", color: "#ccc" }}
                ></i>
                <p className="text-muted mt-3">{t("tracker.noTrackers")}</p>
              </div>
            ) : (
              <>
                {groupedTrackers.map(({ monthYear, sortKey, trackers: monthTrackers }) => (
                  <MonthCard
                    key={monthYear}
                    monthYear={monthYear}
                    trackers={monthTrackers}
                    notificationCount={getMonthNotificationCount(monthTrackers)}
                    onDelete={handleDeleteTracker}
                    onDeleteMonth={async (year: number, month: number) => {
                      setDeleteMonthYear(year);
                      setDeleteMonthMonth(month);
                      setDeleteMonthLabel(monthYear);
                      setShowConfirmDeleteMonth(true);
                    }}
                    onShowChart={handleShowChart}
                    onEditFlight={handleEditFlight}
                    onThresholdUpdated={handleThresholdUpdated}
                  />
                ))}
                {addingTracker && (
                  <div className="card mb-3">
                    <div className="card-body">
                      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '100px' }}>
                        <div className="text-center">
                          <div className="spinner-border text-primary mb-2" role="status">
                            <span className="visually-hidden">Loading tracker...</span>
                          </div>
                          <p className="text-muted small">Loading new tracker...</p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </Col>
        </Row>
      </Container>

      <AddTrackerModal
        show={showAddTracker}
        onHide={() => setShowAddTracker(false)}
        onTrackerAdded={handleTrackerAdded}
      />

      <CheapestDaysModal
        show={showCheapestDays}
        onHide={() => setShowCheapestDays(false)}
        onTrackerAdded={handleTrackerAdded}
      />

      <FlexibleSearchModal
        show={showFlexibleSearch}
        onHide={() => setShowFlexibleSearch(false)}
        onTrackerAdded={() => loadTrackers(true)}
      />

      {selectedTracker && (
        <PriceChartModal
          show={showPriceChart}
          onHide={() => setShowPriceChart(false)}
          tracker={selectedTracker}
        />
      )}

      {selectedTracker && selectedTracker.flight_details && (
        <FlightSelectionModal
          show={showFlightSelection}
          onHide={() => setShowFlightSelection(false)}
          trackerId={selectedTracker.id}
          flightType={editingFlightType}
          currentFlight={
            editingFlightType === "outbound"
              ? selectedTracker.flight_details.outbound
              : selectedTracker.flight_details.return || undefined
          }
          onFlightSelected={handleFlightSelected}
          trackerDepartureDate={selectedTracker.departure_date}
          trackerReturnDate={selectedTracker.return_date || undefined}
        />
      )}

      <ConfirmDialog
        show={showConfirmDelete}
        title={t("common.confirmDeleteTitle")}
        message={t("common.confirmDeleteMessage")}
        confirmText={t("common.confirmText")}
        cancelText={t("common.cancelText")}
        confirmVariant="danger"
        onConfirm={confirmDelete}
        onCancel={() => setShowConfirmDelete(false)}
      />
      <ConfirmDialog
        show={showConfirmDeleteMonth}
        title={t("common.confirmDeleteTitle")}
        message={deleteMonthLabel ? `${t("common.confirmDeleteMessage")} ${deleteMonthLabel}` : t("common.confirmDeleteMessage")}
        confirmText={t("common.confirmText")}
        cancelText={t("common.cancelText")}
        confirmVariant="danger"
        onConfirm={async () => {
          if (!deleteMonthYear || !deleteMonthMonth) {
            setShowConfirmDeleteMonth(false);
            return;
          }
          try {
            await axios.delete(`/api/trackers/month/${deleteMonthYear}/${deleteMonthMonth}`);
            toast.success(`${t("messages.trackerDeleted")} (${deleteMonthLabel})`);
            // Optimization: Don't load flight details after bulk deletion (load_flights=false)
            await loadTrackers(false);
          } catch (err) {
            console.error("Error deleting month trackers:", err);
            toast.error(t("messages.trackerDeleteError"));
          } finally {
            setShowConfirmDeleteMonth(false);
            setDeleteMonthYear(null);
            setDeleteMonthMonth(null);
            setDeleteMonthLabel("");
          }
        }}
        onCancel={() => setShowConfirmDeleteMonth(false)}
      />
      <Footer />
    </>
  );
};

export default Dashboard;
